import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IBase, ISelf } from '@shared/models/api/base';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { catchError, defer, forkJoin, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { CrudService } from '../crud.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { ICreateGrouping, IGrouping, PalletisingService, TGroupingType } from '../palletising.service';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Groupings';
const importConcurrency = 5;

const ALLOWED_TYPES: TGroupingType[] = ['Type', 'Dimension'];

const NAME_PATTERN = new RegExp(ValidatorPatterns.DanishCharacters);
const DESCRIPTION_PATTERN = new RegExp(ValidatorPatterns.DanishCharacters);

export interface IGroupingExportData {
	name: string;
	description?: string;
	type: string;
}

const GroupingSchema: IFileDataSchema<IGroupingExportData> = {
	name: sheetName,
	properties: {
		name: { type: 'string', required: true, fileKey: 'Name', width: 30 },
		description: { type: 'string', required: false, fileKey: 'Description', width: 50 },
		type: { type: 'string', required: true, fileKey: 'Type', width: 15 },
	},
};

/** The roster entry a file row is matched against - only what identity-matching needs, not the full IGrouping the live table displays (which also carries display-only updatedDate/updatedBy). */
interface RosterGrouping {
	self: string;
	name: string;
	type: TGroupingType;
}

interface GroupingImportContext {
	rosterKeyMap: Map<string, RosterGrouping>;
}

/** A row that passed validation, carrying its file location through to a save failure too. */
interface GroupingImportCandidate {
	grouping: ICreateGrouping;
	location?: string;
}

interface IGroupingUpdatePayload extends ISelf {
	name: string;
	description: string | null;
}

@Injectable({
	providedIn: 'root',
})
export class GroupingTransferService implements ITransferService<IGrouping> {
	private translationPrefix = 'masterdata.palletising.groupingImportExport';

	constructor(
		private palletisingService: PalletisingService,
		private crudService: CrudService,
		private excelFileTransfer: ExcelFileTransfer<IGroupingExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	// Grouping name uniqueness is scoped per type - a Dimension and a Type can share a name, since
	// pallets resolve their dimension/type against two separate rosters (see
	// palletisingPalletTransfer.service.ts's validateAndMapReferences). So, like rack/wagon, nothing
	// here treats name as unique on faith: both the file and the existing roster are checked, per type.
	public exportData(groupings: IGrouping[]): Observable<IGrouping[]> {
		const duplicateNames = findDuplicateNames(groupings);
		if (duplicateNames.length > 0) {
			return throwError(
				() =>
					new Error(
						this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, {
							names: duplicateNames.join(', '),
						}),
					),
			);
		}

		const data = groupings.map((grouping) => this.toExportRow(grouping));

		// ExcelFileTransfer.exportData writes the file (and validates cell lengths) synchronously
		// before returning its observable, so calling it directly here would let a real write
		// failure throw past catchError below instead of reaching it - defer() delays that call
		// until subscription, inside RxJS's own error handling.
		return defer(() => this.excelFileTransfer.exportData(sheetName, data, GroupingSchema)).pipe(
			map(() => groupings),
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	public downloadTemplate(): void {
		const exampleRow: IGroupingExportData = {
			name: 'Example Dimension',
			description: 'Example description',
			type: 'Dimension',
		};

		this.excelFileTransfer.exportData(`${sheetName}Template`, [exampleRow], GroupingSchema).subscribe();
	}

	private toExportRow(grouping: IGrouping): IGroupingExportData {
		return {
			name: grouping.name,
			description: grouping.description || '',
			type: grouping.type,
		};
	}

	// Duplicate names (file or roster, scoped per type) and per-row validation problems all get
	// discovered and reported together in one pass, instead of a user fixing one category,
	// re-importing, and only then discovering the next.
	public importData(file: File | File[]): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				// Nothing parsed - whatever went wrong is already in ImportErrors. Stop here instead
				// of fetching the roster only to report an unrelated duplicate-name error that would
				// bury the actual parse failure.
				if (parsedRows.length === 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateNames(parsedRows.map((imported) => imported.data));

				return forkJoin([this.palletisingService.fetchPalletDimensions(), this.palletisingService.fetchPalletTypes()]).pipe(
					mergeMap(([dimensions, types]) => {
						const roster: RosterGrouping[] = [...dimensions.items.map((item) => this.toRosterEntry(item, 'Dimension')), ...types.items.map((item) => this.toRosterEntry(item, 'Type'))];
						const rosterDuplicates = findDuplicateNames(roster);

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0;

						const ctx: GroupingImportContext = {
							rosterKeyMap: buildRosterKeyMap(roster),
						};

						return from(parsedRows).pipe(
							filterMap(this.validateRow(ImportErrors)),
							toArray(),
							mergeMap((validRows) => (namesAreAmbiguous ? of([]) : from(validRows).pipe(mergeMap(this.createOrUpdateGrouping(ctx, ImportErrors), importConcurrency), toArray()))),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
					// Without this, a failure fetching either roster (network drop, transient 5xx)
					// would escape the ImportResponse contract every other failure path in this
					// function follows - discarding any parse errors already collected and surfacing a
					// raw, untranslated error instead of the itemized popup.
					catchError((error) => {
						console.error(error);
						ImportErrors.push({
							status: ImportStatus.ParsingError,
							message: this.translator.instant(`${this.translationPrefix}.import.errors.fetch-roster-failed`),
						} as FailedParse);
						return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
					}),
				);
			}),
		);
	}

	private toRosterEntry(item: IBase, type: TGroupingType): RosterGrouping {
		return { self: item.self, name: item.name, type };
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<IGroupingExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, GroupingSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	// Mirrors every validator the create/edit dialog applies to its form (name/description patterns
	// and lengths, the closed type list), since import is a second, dialog-bypassing entry point
	// into the same data.
	private validateRow(ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<IGroupingExportData>): GroupingImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			const name = (row.name ?? '').trim();
			if (!name) {
				errors['name'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-name`),
				};
			} else if (name.length > 20) {
				errors['name'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.name-too-long`),
				};
			} else if (!NAME_PATTERN.test(name)) {
				errors['name'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.name-invalid-characters`),
				};
			}

			const description = (row.description ?? '').trim();
			if (description.length > 100) {
				errors['description'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.description-too-long`),
				};
			} else if (description && !DESCRIPTION_PATTERN.test(description)) {
				errors['description'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.description-invalid-characters`),
				};
			}

			const type = (row.type ?? '').trim();
			if (!type) {
				errors['type'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-type`),
				};
			} else if (!ALLOWED_TYPES.includes(type as TGroupingType)) {
				errors['type'] = {
					invalid: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-type`, { value: type }),
				};
			}

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: name || '',
					location,
					status: ImportStatus.ValidationError,
					errors,
				} as FailedValidationImport);
				return undefined;
			}

			const grouping: ICreateGrouping = {
				name,
				description: description || null,
				type: type as TGroupingType,
			};

			return { grouping, location };
		};
	}

	private createOrUpdateGrouping(ctx: GroupingImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: GroupingImportCandidate) => {
			const { grouping, location } = candidate;
			// Safe: importData() has already confirmed neither the file nor the roster has a
			// (name, type) collision, so at most one existing grouping can match here.
			const existing = ctx.rosterKeyMap.get(rosterKey(grouping));

			if (existing) {
				const payload: IGroupingUpdatePayload = {
					self: existing.self,
					name: grouping.name,
					description: grouping.description,
				};
				return this.crudService.update<IBase>(payload).pipe(
					map(() => ({ entity: grouping, status: ImportStatus.SuccessUpdate })),
					catchError((error) => {
						this.pushSaveFailedError(ImportErrors, grouping, location, error);
						return of();
					}),
				);
			}

			return this.palletisingService.createGrouping(grouping).pipe(
				map(() => ({ entity: grouping, status: ImportStatus.SuccessCreate })),
				catchError((error) => {
					this.pushSaveFailedError(ImportErrors, grouping, location, error);
					return of();
				}),
			);
		};
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], grouping: ICreateGrouping, location: string | undefined, error: unknown) {
		ImportErrors.push({
			Id: grouping.name,
			location,
			status: ImportStatus.Failed,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.save-failed`, {
				name: grouping.name,
				msg: (error as Error)?.message,
			}),
		});
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}
}

/**
 * A grouping's real identity: the same name may legitimately exist once per type (a Dimension and a Type are different rosters as far as pallets are concerned). Both fields are normalized the same way - findDuplicateNames() runs this on raw, unvalidated file rows (before validateRow() trims `type`), so an untrimmed
 * "Dimension " and a clean "Dimension" must still collide here, exactly as they will once validateRow() normalizes them into identical save candidates. JSON-encoded rather than joined with a plain separator as cheap insurance against a delimiter collision, since name carries no character restriction guarantee here
 * beyond the DanishCharacters pattern.
 */
function rosterKey(item: { name: string; type: string }): string {
	return JSON.stringify([normalizeName(item.type), normalizeName(item.name)]);
}

function buildRosterKeyMap(items: RosterGrouping[]): Map<string, RosterGrouping> {
	const map = new Map<string, RosterGrouping>();
	items.forEach((item) => map.set(rosterKey(item), item));
	return map;
}

/** Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns the original (non-normalized) spelling of every row involved in a collision, for a message a user can act on. */
function findDuplicateNames(items: { name: string; type: string }[]): string[] {
	const firstSeen = new Map<string, string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		const normalized = normalizeName(item.name);
		if (!normalized) {
			continue;
		}

		const key = rosterKey(item);
		const original = firstSeen.get(key);
		if (original !== undefined) {
			duplicates.add(original);
			duplicates.add(item.name);
		} else {
			firstSeen.set(key, item.name);
		}
	}

	return Array.from(duplicates);
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}
